import React from 'react';
import { GeneratorFactory } from '../utils';
import { SimpleSubject } from '../subject';

export enum Model {
  float = 'float',
  fillAvailable = 'fill-available',
  transparent = 'transparent',
  positionByAnchor = 'position-by-anchor',
  fixed = 'fixed'
}

export enum Position {
  auto = 'auto',
  left = 'left',
  top = 'top',
  bottom = 'bottom',
  right = 'right',
  // leftStrict: 'leftStrict',
  topStrict = 'topStrict',
  bottomStrict = 'bottomStrict'
  // rightStrict: 'rightStrict'
}

export enum State {
  init = 1,
  opened = 2,
  closed = 3
}

export const DIALOG_CANCEL_CLOSE = 'dialog-cancel-close';
const NewID = GeneratorFactory('dialog_id_');

export class Dialog<R = void, D = void> {
  id: string;
  _state: SimpleSubject<State>;
  _result: R | undefined;
  config: DialogConfig<R, D> | undefined;
  _vm;

  constructor(config?: DialogConfig<R, D>) {
    this.id = NewID();
    this.config = config;
    this._state = new SimpleSubject();
    this._state.next(State.init);
  }

  close(dialogResult?: R): Promise<void> {
    return new Promise((r, j) => {
      try {
        const tempClose = (newResult?: R) => {
          try {
            this._result = newResult;
            this._state.next(State.closed);
            this._state.complete();
            r();
          } catch (err1) {
            j(err1);
          }
        };
        if (this.config?.beforeClose) {
          Promise.resolve(
            this.config.beforeClose(dialogResult)
          ).then(res => {
            if(res !== DIALOG_CANCEL_CLOSE) {
              tempClose(res);
            }
          });
        } else {
          tempClose(dialogResult);
        }
      } catch (err) {
        j(err);
      }
    });
  }

  error(msg) {
    this._state.error(msg);
  }

  setOpen(vm) {
    this._vm = vm;
    this._state.next(State.opened);
  }

  afterOpened(): Promise<void> {
    return new Promise((r, j) => {
      try {
        const sub = this._state.subscribe((c) => {
          if (c === State.opened) {
            sub.unsubscribe();
            r();
          }
        }, (err) => {
          j(err);
        });
      } catch (e) {
        j(e);
      }
    });
  }

  afterClosed(): Promise<R | undefined> {
    return new Promise((r, j) => {
      try {
        const sub = this._state.subscribe((c) => {
          if (c === State.closed) {
            sub.unsubscribe();
            r(this._result);
          }
        }, (err) => {
          j(err);
        });
      } catch (e) {
        j(e);
      }
    });
  }
}

export interface DialogItem<R, D> {
  instance: Dialog<R, D>;
  component?: (props: { dialog: Dialog<R, D>, [key: string]: any }) => React.ReactElement;
  // parent: any
}

export interface DialogConfig<R, D> {
  data?: D | null;
  beforeClose?: (data?: R) => R | typeof DIALOG_CANCEL_CLOSE | Promise<R | typeof DIALOG_CANCEL_CLOSE>;
  disableClose?: boolean; // Whether the user can click on the backdrop to close the modal, same to 'close-on-click-overlay'
  disableEscClose?: boolean; // Whether the user can use escape to close the modal
  model?: Model;
  backgroundCover?: boolean; // undefined - auto cover: anchor position - no cover, others - cover; true - cover always; false - no cover always
  [key: string]: any;
}
